import { useState, useRef } from "react";
import { trpc } from "../../trpc";
import SectionTitle from "../../components/SectionTitle";
import { AgentWizard } from "../../components/agent/AgentWizard";
import { AgentTypeSelection } from "../../components/agent/AgentTypeSelection";
import RoutingAgentWizard from "../../components/agent/RoutingAgentWizard";
import { ExternalAgentWizard } from "../../components/agent/ExternalAgentWizard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

type Agent = {
  id: string;
  merchantId: string;
  config?: {
    type: string;
    version: string;
    systemPrompt?: string;
    tools?: string[];
  };
  fallback?: boolean;
  project?: {
    id: string;
    name: string;
  } | null;
  topic?: string;
  agentType?: "internal" | "external";
  external?: {
    webhookUrl: string;
  };
};

const AgentCreatorContainer = () => {
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>("");
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [editedSystemPrompt, setEditedSystemPrompt] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);
  const [isProjectWizardOpen, setIsProjectWizardOpen] = useState(false);
  const [isRoutingWizardOpen, setIsRoutingWizardOpen] = useState(false);
  const [isExternalWizardOpen, setIsExternalWizardOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const merchantsQuery = trpc.merchant.merchants.useQuery();
  const { data: merchants, isLoading: merchantsLoading, error: merchantsError } = merchantsQuery;

  const agentsQuery = trpc.agent.agents.useQuery(
    { merchantId: selectedMerchantId },
    { enabled: !!selectedMerchantId }
  );
  const { data: agents, isLoading: agentsLoading, error: agentsError } = agentsQuery;

  const updateAgentMutation = trpc.agent.updateAgent.useMutation({
    onSuccess: (updatedAgent) => {
      toast.success("El system prompt ha sido actualizado correctamente");
      setIsEditing(false);
      setIsConfirmOpen(false);
      setSelectedAgent(updatedAgent);
      agentsQuery.refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al actualizar el system prompt");
    },
  });

  const handleMerchantChange = (merchantId: string) => {
    setSelectedMerchantId(merchantId);
  };

  const handleAgentClick = (agent: Agent) => {
    setSelectedAgent(agent);
    setEditedSystemPrompt(agent.config?.systemPrompt || "");
    setIsEditing(false);
    setIsOpen(true);
  };

  const handleSaveSystemPrompt = async () => {
    if (!selectedAgent) return;

    updateAgentMutation.mutate({
      merchantId: selectedAgent.merchantId,
      agentId: selectedAgent.id,
      systemPrompt: editedSystemPrompt.trim(),
    });
  };

  const getAgentName = (agent: Agent) => {
    if (agent.project?.name) {
      return agent.project.name;
    }
    if (agent.fallback) {
      return "Fallback";
    }
    if (agent.topic) {
      return agent.topic;
    }
    return "Agente";
  };

  const getAgentType = (agent: Agent) => {
    if (agent.agentType === "external") {
      return "Externo";
    }
    if (agent.project?.name) {
      return "Comercial";
    }
    if (agent.fallback) {
      return "Enrutador";
    }
    if (agent.topic) {
      return agent.topic;
    }
    return "Default";
  };

  const handleWizardSuccess = () => {
    agentsQuery.refetch();
    merchantsQuery.refetch();
  };

  const handleAgentTypeSelection = (type: "project" | "routing" | "external") => {
    if (type === "project") {
      setIsProjectWizardOpen(true);
    } else if (type === "routing") {
      setIsRoutingWizardOpen(true);
    } else {
      setIsExternalWizardOpen(true);
    }
  };

  return (
    <div className="p-6 mx-auto w-full">
      <div className="flex flex-col gap-6 w-full">
        <div className="flex justify-between items-center">
          <SectionTitle title="Agentes" />
          <Button onClick={() => setIsTypeSelectionOpen(true)}>
            Nuevo
          </Button>
        </div>

        <div>
          {merchantsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="h-6 w-6" />
            </div>
          ) : merchantsError ? (
            <Alert status="error">
              Error loading merchants: {merchantsError?.message || "Unknown error"}
            </Alert>
          ) : (
            <SearchableSelect
              value={selectedMerchantId}
              onValueChange={handleMerchantChange}
              placeholder="Selecciona un merchant..."
              searchPlaceholder="Buscar merchant..."
              items={merchants?.map((merchant) => ({
                value: merchant.id,
                label: `${merchant.id} (${merchant.type})`,
              })) || []}
              triggerClassName="max-w-[400px]"
            />
          )}
        </div>

        {selectedMerchantId && (
          <div>
            <p className="mb-4 font-medium text-lg">
              Agentes del merchant: {selectedMerchantId}
            </p>

            {agentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-8 w-8" />
              </div>
            ) : agentsError ? (
              <Alert status="error">
                Error loading agents: {agentsError?.message || "Unknown error"}
              </Alert>
            ) : agents && agents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {agents.map((agent) => (
                  <Card
                    key={agent.id}
                    className="cursor-pointer hover:-translate-y-1 hover:shadow-lg border-gray-200 transition-all"
                    onClick={() => handleAgentClick(agent)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-center w-full">
                          <h3 className="text-md font-semibold text-primary">
                            {getAgentName(agent)}
                          </h3>
                          <Badge variant={agent.fallback ? "secondary" : "default"} className="text-xs">
                            {getAgentType(agent)}
                          </Badge>
                        </div>

                        <p className="text-xs text-muted-foreground">
                          Click para ver la configuración del agente
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">No agents found for this merchant</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Agente - {selectedAgent ? getAgentName(selectedAgent) : ""}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {selectedAgent && (
              <div className="flex flex-col gap-4">
                {selectedAgent.config?.tools && selectedAgent.config.tools.length > 0 && (
                  <div>
                    <p className="font-bold mb-2">Herramientas:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedAgent.config.tools.map((tool, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex justify-between mb-2">
                    <p className="font-bold">System Prompt:</p>
                    {!isEditing && selectedAgent.config?.systemPrompt && (
                      <Button size="sm" onClick={() => setIsEditing(true)}>
                        Editar
                      </Button>
                    )}
                  </div>
                  {isEditing ? (
                    <Textarea
                      value={editedSystemPrompt}
                      onChange={(e) => setEditedSystemPrompt(e.target.value)}
                      placeholder="Escribe el system prompt aquí..."
                      className="min-h-[400px] resize-y font-mono text-sm"
                    />
                  ) : (
                    <div className="bg-muted p-4 rounded-md border max-h-[400px] overflow-y-auto">
                      <p className="text-sm whitespace-pre-wrap font-mono">
                        {selectedAgent.config?.systemPrompt ||
                          "No visible, la versión del agente debe ser actualizada"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <div className="flex gap-3">
              {isEditing && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedSystemPrompt(selectedAgent?.config?.systemPrompt || "");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => setIsConfirmOpen(true)}
                    disabled={!editedSystemPrompt.trim()}
                  >
                    Guardar
                  </Button>
                </>
              )}
              <Button variant="secondary" onClick={() => setIsOpen(false)}>
                Cerrar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambios</AlertDialogTitle>
          </AlertDialogHeader>
          <p className="py-4">
            ¿Estás seguro de que quieres actualizar el system prompt de este agente? Esta acción
            tendrá efecto inmediato.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveSystemPrompt}
              disabled={updateAgentMutation.isLoading}
            >
              {updateAgentMutation.isLoading ? "Guardando..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AgentTypeSelection
        isOpen={isTypeSelectionOpen}
        onClose={() => setIsTypeSelectionOpen(false)}
        onSelectType={handleAgentTypeSelection}
      />

      <AgentWizard
        isOpen={isProjectWizardOpen}
        onClose={() => setIsProjectWizardOpen(false)}
        onSuccess={handleWizardSuccess}
      />

      <RoutingAgentWizard
        isOpen={isRoutingWizardOpen}
        onClose={() => setIsRoutingWizardOpen(false)}
        onSuccess={handleWizardSuccess}
      />

      <ExternalAgentWizard
        isOpen={isExternalWizardOpen}
        onClose={() => setIsExternalWizardOpen(false)}
        onSuccess={handleWizardSuccess}
      />
    </div>
  );
};

export default AgentCreatorContainer;
